import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Eye, Trash2, ShoppingCart, AlertTriangle } from "lucide-react";

const ESTADOS = {
  pendiente: { label: "Pendiente", className: "bg-amber-100 text-amber-900 border-amber-300" },
  aprobada: { label: "Aprobada", className: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  rechazada: { label: "Rechazada", className: "bg-rose-100 text-rose-900 border-rose-300" },
};

export default function SolicitudesCompra() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filtroEstado, setFiltroEstado] = useState("pendiente");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const [proveedores, setProveedores] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [decisionMode, setDecisionMode] = useState(null); // 'aprobar' | 'rechazar'
  const [decisionForm, setDecisionForm] = useState({ motivo_rechazo: "", observaciones_aprobador: "", generar_ordenes_compra: true });
  const [editLineas, setEditLineas] = useState([]);

  const load = async () => {
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (filtroEstado) params.estado = filtroEstado;
      const { data } = await api.get("/solicitudes-compra", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filtroEstado]);
  useEffect(() => {
    api.get("/masters/proveedores").then(({ data }) => setProveedores(data || [])).catch(() => {});
  }, []);

  const proveedoresMap = useMemo(() => {
    const m = {};
    proveedores.forEach((p) => { m[p.id] = p.razon_social || p.nombre_comercial; });
    return m;
  }, [proveedores]);

  const openDetalle = async (sol) => {
    // Optimistic: mostrar de inmediato con los datos del list mientras se
    // trae el detalle enriquecido (con ultimo_costo).
    setDetalle(sol);
    setEditLineas((sol.lineas || []).map((li) => ({ ...li })));
    setDecisionMode(null);
    setDecisionForm({ motivo_rechazo: "", observaciones_aprobador: "", generar_ordenes_compra: true });
    try {
      const { data: fresh } = await api.get(`/solicitudes-compra/${sol.id}`);
      setDetalle(fresh);
      setEditLineas((fresh.lineas || []).map((li) => ({ ...li })));
    } catch (e) {
      // Si falla, se mantienen los datos del list (sin ultimo_costo)
      console.warn("No se pudo obtener el detalle enriquecido", e);
    }
  };

  const updateLinea = (idx, field, value) => {
    const next = [...editLineas];
    next[idx] = { ...next[idx], [field]: value };
    setEditLineas(next);
  };

  const eliminarLinea = (idx) => {
    const li = editLineas[idx];
    if (!window.confirm(`¿Eliminar el producto "${li?.producto_nombre || 'sin nombre'}" de esta solicitud?\n\nEsta acción no se puede deshacer una vez guardada.`)) return;
    setEditLineas(prev => prev.filter((_, i) => i !== idx));
  };

  const guardarLineas = async () => {
    if (!detalle) return;
    try {
      const payload = {
        lineas: editLineas.map((li) => ({
          producto_comercial_id: li.producto_comercial_id,
          producto_nombre: li.producto_nombre,
          cantidad_requerida: Number(li.cantidad_requerida) || 0,
          stock_actual: Number(li.stock_actual) || 0,
          cantidad_a_comprar: Number(li.cantidad_a_comprar) || 0,
          motivo: li.motivo,
          laboratorio_nombre: li.laboratorio_nombre,
          presentacion: li.presentacion,
          precio_referencia: Number(li.precio_referencia) || 0,
          proveedor_sugerido_id: li.proveedor_sugerido_id || null,
          proveedor_sugerido_nombre: li.proveedor_sugerido_id ? proveedoresMap[li.proveedor_sugerido_id] : null,
          // Feb 2026: preservar unidad de venta (caja/unidad)
          unidad_venta: li.unidad_venta || null,
          unidades_por_caja: li.unidades_por_caja || null,
          es_caja: !!li.es_caja,
        })),
      };
      const { data } = await api.patch(`/solicitudes-compra/${detalle.id}`, payload);
      toast.success("Cambios guardados");
      setDetalle(data);
      setEditLineas((data.lineas || []).map((li) => ({ ...li })));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ejecutarDecision = async () => {
    if (!detalle || !decisionMode) return;
    try {
      // primero asegurar que las líneas (con proveedor sugerido editado) están guardadas
      await guardarLineas();
      const url = decisionMode === "aprobar"
        ? `/solicitudes-compra/${detalle.id}/aprobar`
        : `/solicitudes-compra/${detalle.id}/rechazar`;
      const payload = {
        motivo_rechazo: decisionForm.motivo_rechazo || null,
        observaciones_aprobador: decisionForm.observaciones_aprobador || null,
        generar_ordenes_compra: decisionMode === "aprobar" ? !!decisionForm.generar_ordenes_compra : false,
      };
      const { data } = await api.post(url, payload);
      if (decisionMode === "aprobar") {
        const ocs = data?.ordenes_compra || [];
        if (ocs.length) {
          toast.success(`Aprobada · ${ocs.length} OC(s) generada(s)`);
        } else {
          toast.success("Solicitud aprobada");
        }
      } else {
        toast.success("Solicitud rechazada");
      }
      setDetalle(null);
      setDecisionMode(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar solicitud?")) return;
    try {
      await api.delete(`/solicitudes-compra/${id}`);
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="solicitudes-compra-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Solicitudes de compra</h1>
          <p className="text-sm text-muted-foreground">Gerencia revisa, edita y aprueba las solicitudes generadas por faltantes de stock al convertir cotizaciones.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filtroEstado || "all"} onValueChange={(v) => { setPage(0); setFiltroEstado(v === "all" ? "" : v); }}>
            <SelectTrigger className="w-44" data-testid="solicitudes-filtro-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="pendiente">Pendientes</SelectItem>
              <SelectItem value="aprobada">Aprobadas</SelectItem>
              <SelectItem value="rechazada">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-3 py-2">N°</th>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Vendedor</th>
              <th className="px-3 py-2">Cotización</th>
              <th className="px-3 py-2 text-center">Líneas</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">OCs generadas</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No hay solicitudes</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="border-t" data-testid={`solicitud-row-${s.numero}`}>
                <td className="px-3 py-2 font-mono">{s.numero}</td>
                <td className="px-3 py-2">{(s.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">{s.cliente_nombre || "—"}</td>
                <td className="px-3 py-2">{s.vendedor_nombre || "—"}</td>
                <td className="px-3 py-2">{s.cotizacion_numero || "—"}</td>
                <td className="px-3 py-2 text-center">{(s.lineas || []).length}</td>
                <td className="px-3 py-2">
                  <Badge className={`border ${ESTADOS[s.estado]?.className || ""}`}>{ESTADOS[s.estado]?.label || s.estado}</Badge>
                </td>
                <td className="px-3 py-2 text-xs">
                  {(s.ordenes_compra_generadas || []).length === 0 ? "—" : (
                    <ul className="space-y-0.5">
                      {s.ordenes_compra_generadas.map((o) => (
                        <li key={o.id}><span className="font-mono text-emerald-700">{o.numero}</span> · {o.proveedor_nombre} · ${Number(o.total).toFixed(2)}</li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openDetalle(s)} data-testid={`solicitud-ver-${s.numero}`}><Eye className="h-3.5 w-3.5" /></Button>
                  {s.estado === "pendiente" && (
                    <Button size="sm" variant="ghost" onClick={() => eliminar(s.id)} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span>Página {page + 1} de {Math.ceil(total / PAGE_SIZE)}</span>
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>Anterior</Button>
          <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </div>
      )}

      <Dialog open={!!detalle} onOpenChange={(v) => { if (!v) { setDetalle(null); setDecisionMode(null); } }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              Solicitud {detalle?.numero} ·{" "}
              <Badge className={`border ml-1 ${ESTADOS[detalle?.estado]?.className || ""}`}>{ESTADOS[detalle?.estado]?.label}</Badge>
            </DialogTitle>
            <DialogDescription>
              Ajuste las cantidades a comprar, elimine productos que no se van a comprar y decida aprobar o rechazar la solicitud.
            </DialogDescription>
          </DialogHeader>

          {detalle && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><span className="text-muted-foreground">Cliente:</span> <b>{detalle.cliente_nombre || "—"}</b></div>
                <div><span className="text-muted-foreground">Vendedor:</span> <b>{detalle.vendedor_nombre || "—"}</b></div>
                <div><span className="text-muted-foreground">Cotización:</span> <b>{detalle.cotizacion_numero || "—"}</b></div>
                <div><span className="text-muted-foreground">Pedido:</span> <b>{detalle.pedido_numero || "—"}</b></div>
                {detalle.aprobador_nombre && (
                  <div className="col-span-full"><span className="text-muted-foreground">Aprobada/rechazada por:</span> <b>{detalle.aprobador_nombre}</b> el {(detalle.fecha_decision || "").slice(0, 16).replace("T", " ")}</div>
                )}
                {detalle.motivo_rechazo && (
                  <div className="col-span-full text-rose-700"><AlertTriangle className="h-4 w-4 inline" /> Motivo rechazo: {detalle.motivo_rechazo}</div>
                )}
              </div>

              <div className="rounded-lg border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      <th className="px-2 py-1.5">Producto</th>
                      <th className="px-2 py-1.5">Laboratorio</th>
                      <th className="px-2 py-1.5">Unidad</th>
                      <th className="px-2 py-1.5 text-right">Solicitado</th>
                      <th className="px-2 py-1.5 text-right">Stock</th>
                      <th className="px-2 py-1.5 text-right">A comprar</th>
                      <th className="px-2 py-1.5 text-right">Último costo</th>
                      <th className="px-2 py-1.5 text-right">Precio ref.</th>
                      <th className="px-2 py-1.5">Proveedor sugerido</th>
                      {detalle.estado === "pendiente" && <th className="px-2 py-1.5 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {editLineas.length === 0 && (
                      <tr>
                        <td colSpan={detalle.estado === "pendiente" ? 10 : 9} className="text-center py-6 text-muted-foreground">
                          Sin productos. La solicitud quedará vacía si se guarda así.
                        </td>
                      </tr>
                    )}
                    {editLineas.map((li, idx) => {
                      const editable = detalle.estado === "pendiente";
                      const esCaja = !!li.es_caja || (li.unidad_venta || "").toLowerCase() === "caja";
                      const upc = Number(li.unidades_por_caja) || 1;
                      return (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{li.producto_nombre}</div>
                            {li.presentacion && <div className="text-muted-foreground">{li.presentacion}</div>}
                          </td>
                          <td className="px-2 py-1.5">{li.laboratorio_nombre || "—"}</td>
                          <td className="px-2 py-1.5">
                            {esCaja ? (
                              <Badge className="bg-blue-100 text-blue-900 border border-blue-300" data-testid={`solicitud-badge-caja-${idx}`}>
                                Caja{upc > 1 ? ` ×${upc}u` : ""}
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-900 border border-slate-300" data-testid={`solicitud-badge-unidad-${idx}`}>
                                Unidad
                              </Badge>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {Number(li.cantidad_requerida).toFixed(esCaja ? 2 : 0)} {esCaja ? "cj" : "u"}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {Number(li.stock_actual).toFixed(esCaja ? 2 : 0)} {esCaja ? "cj" : "u"}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            {editable ? (
                              <Input type="number" step={esCaja ? "0.01" : "1"} className="h-7 w-20 text-right tabular-nums" value={li.cantidad_a_comprar}
                                onChange={(e) => updateLinea(idx, "cantidad_a_comprar", e.target.value)}
                                data-testid={`solicitud-cant-${idx}`} />
                            ) : (
                              <span className="tabular-nums">{Number(li.cantidad_a_comprar).toFixed(esCaja ? 2 : 0)} {esCaja ? "cj" : "u"}</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right" data-testid={`solicitud-ult-costo-${idx}`}>
                            {li.ultimo_costo != null ? (
                              <div className="flex flex-col items-end">
                                <span className="tabular-nums font-medium text-slate-800">${Number(li.ultimo_costo).toFixed(2)}</span>
                                {li.ultimo_costo_fecha && (
                                  <span className="text-[10px] text-muted-foreground">{String(li.ultimo_costo_fecha).slice(0, 10)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-[10px]">Sin compras previas</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            {editable ? (
                              <Input type="number" step="0.01" className="h-7 w-24 text-right tabular-nums" value={li.precio_referencia || 0}
                                onChange={(e) => updateLinea(idx, "precio_referencia", e.target.value)} />
                            ) : (
                              <span className="tabular-nums">${Number(li.precio_referencia || 0).toFixed(2)}</span>
                            )}
                            {(() => {
                              const uc = Number(li.ultimo_costo) || 0;
                              const pr = Number(li.precio_referencia) || 0;
                              if (uc > 0 && pr > 0 && pr > uc) {
                                const pct = ((pr - uc) / uc) * 100;
                                if (pct >= 5) {
                                  return (
                                    <div
                                      className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                        pct >= 20
                                          ? "bg-rose-100 text-rose-800 border border-rose-300"
                                          : "bg-amber-100 text-amber-900 border border-amber-300"
                                      }`}
                                      title={`El precio de compra sugerido es ${pct.toFixed(1)}% mayor al último costo pagado. Considere renegociar antes de aprobar.`}
                                      data-testid={`solicitud-precio-alerta-${idx}`}
                                    >
                                      <AlertTriangle size={10} />
                                      +{pct.toFixed(0)}% vs. último
                                    </div>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </td>
                          <td className="px-2 py-1.5">
                            {editable ? (
                              <Select value={li.proveedor_sugerido_id || "auto"} onValueChange={(v) => updateLinea(idx, "proveedor_sugerido_id", v === "auto" ? "" : v)}>
                                <SelectTrigger className="h-7 w-56"><SelectValue placeholder="Auto sugerir" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="auto">Auto sugerir (lab/lote)</SelectItem>
                                  {proveedores.slice(0, 200).map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.razon_social || p.nombre_comercial}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span>{li.proveedor_sugerido_nombre || "—"}</span>
                            )}
                          </td>
                          {editable && (
                            <td className="px-2 py-1.5 text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-rose-600 hover:text-rose-800 hover:bg-rose-50"
                                onClick={() => eliminarLinea(idx)}
                                title="Quitar producto de la solicitud"
                                data-testid={`solicitud-eliminar-linea-${idx}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {detalle.estado === "pendiente" && !decisionMode && (
                <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={guardarLineas} data-testid="solicitud-guardar">Guardar cambios</Button>
                  <Button variant="destructive" onClick={() => setDecisionMode("rechazar")} data-testid="solicitud-rechazar"><XCircle className="h-4 w-4 mr-1" /> Rechazar</Button>
                  <Button onClick={() => setDecisionMode("aprobar")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="solicitud-aprobar"><CheckCircle2 className="h-4 w-4 mr-1" /> Aprobar</Button>
                </div>
              )}

              {decisionMode && (
                <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
                  <h3 className="font-semibold">{decisionMode === "aprobar" ? "Aprobar solicitud" : "Rechazar solicitud"}</h3>
                  {decisionMode === "rechazar" && (
                    <div>
                      <Label>Motivo de rechazo *</Label>
                      <Textarea value={decisionForm.motivo_rechazo} onChange={(e) => setDecisionForm({ ...decisionForm, motivo_rechazo: e.target.value })} />
                    </div>
                  )}
                  <div>
                    <Label>Observaciones</Label>
                    <Textarea value={decisionForm.observaciones_aprobador} onChange={(e) => setDecisionForm({ ...decisionForm, observaciones_aprobador: e.target.value })} />
                  </div>
                  {decisionMode === "aprobar" && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={decisionForm.generar_ordenes_compra}
                        onChange={(e) => setDecisionForm({ ...decisionForm, generar_ordenes_compra: e.target.checked })} />
                      <ShoppingCart className="h-4 w-4" /> Generar Órdenes de Compra borrador agrupadas por proveedor
                    </label>
                  )}
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setDecisionMode(null)}>Cancelar</Button>
                    <Button onClick={ejecutarDecision}
                      className={decisionMode === "aprobar" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
                      data-testid="solicitud-confirmar-decision">
                      Confirmar {decisionMode}
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {detalle.observaciones_aprobador && detalle.estado !== "pendiente" && (
                <div className="text-sm bg-muted/30 rounded p-3"><b>Observaciones:</b> {detalle.observaciones_aprobador}</div>
              )}

              {(detalle.ordenes_compra_generadas || []).length > 0 && (
                <div className="text-sm">
                  <b>Órdenes de compra generadas:</b>
                  <ul className="mt-1 space-y-0.5">
                    {detalle.ordenes_compra_generadas.map((o) => (
                      <li key={o.id} className="font-mono">{o.numero} · {o.proveedor_nombre} · ${Number(o.total).toFixed(2)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
